import { QuantitySelectorComponent } from '@theme/component-quantity-selector';

/**
 * A custom element that allows the user to select a quantity in the cart.
 * Extends QuantitySelectorComponent but uses absolute max limits instead of effective max.
 * Semantics: "What should the total quantity BE in the cart" vs "How many to ADD to cart"
 *
 * @extends {QuantitySelectorComponent}
 */
export class CartQuantitySelectorComponent extends QuantitySelectorComponent {
  /**
   * Gets the effective maximum value for cart quantity selector
   * Cart page: uses absolute max (how much can be in cart total)
   * @returns {number | null} The effective max, or null if no max
   */
  getEffectiveMax() {
    const { max } = this.getCurrentValues();
    return max; // Cart uses absolute max, not max minus cart quantity
  }

  /**
   * Updates button states based on current value and limits
   * Cart buttons are always managed client-side, never server-disabled
   */
  updateButtonStates() {
    const { minusButton, plusButton } = this.refs;
    const { value } = this.getCurrentValues();
    const effectiveMax = this.getEffectiveMax();

    // Cart buttons are always dynamically managed.
    //
    // Deliberately not `value <= min`. In the cart, minus at the minimum removes
    // the line rather than being a dead control -- stepping a single item down to
    // nothing is what the button visibly promises, and a disabled minus next to a
    // separate bin icon makes the customer hunt for the second control to express
    // the same intent. See decreaseQuantity below.
    minusButton.disabled = value <= 0;
    plusButton.disabled = effectiveMax !== null && value >= effectiveMax;
  }

  /**
   * Decreasing past the minimum removes the line instead of clamping to it.
   * @param {Event} event - The event.
   */
  decreaseQuantity(event) {
    const { min, value } = this.getCurrentValues();
    if (value > min) return super.decreaseQuantity(event);

    event.preventDefault();
    const { quantityInput } = this.refs;
    // `component-cart-items` already reads quantity 0 as a removal and routes it
    // to onLineItemRemove, so this only has to get the value there. The base
    // updateQuantity() cannot: it clamps with Math.max(min, ...).
    quantityInput.value = '0';
    this.onQuantityChange();
    this.updateButtonStates();
  }
}

if (!customElements.get('cart-quantity-selector-component')) {
  customElements.define('cart-quantity-selector-component', CartQuantitySelectorComponent);
}
